import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box } from "@mui/material";
import {
  GridPagination,
  GridToolbarContainer,
  type GridSortModel,
} from "@mui/x-data-grid-pro";
import { AcquireBookDataGrid } from "@/components/DataGrid/AcquireBookDataGrid";
import {
  makeHistoryGridColDef,
  makeHistoryColumnVisibilityModel,
  type PropertyCodePictureRequestGridRow,
} from "@/features/propertyCodeAcquisition/utils/makeHistoryGridColDef";
import { useGridPagination } from "@/features/propertyCodeAcquisition/hooks/useGridPagination";
import type {
  PropertyInfoResponse,
  PropertyCodePictureRequestListResponse,
} from "@/features/propertyCodeAcquisition/types";

interface PropertyCodePictureRequestTableProps {
  history: PropertyCodePictureRequestListResponse | undefined;
  /** 物件種別。建物図面列の表示可否（土地で非表示）に用いる。 */
  realEstateType?: PropertyInfoResponse["realEstateType"];
  isLoading?: boolean;
  /**
   * 取得成功時に新着行を先頭に見せるためのシグナル（成功ごとにインクリメントするカウンタ）。
   * 値が変化した時のみページを先頭へ戻し、ソートも既定（サーバ降順）へ戻す
   * （ユーザーがソート適用中だと先頭ページに新着が来ないため）。ページサイズ選択は維持する。
   */
  resetPageSignal?: number;
}

const ROWS_PER_PAGE_OPTIONS: number[] = [10, 25, 50, 100];

// テーブル本体の固定高さ（standard density の既定メトリクスから算出）。
// 初期ページサイズ（10行）を上下ページャ・列ヘッダー込みで縦スクロールなしに表示し切る高さにする。
// 25/50/100 行選択時は本高さのまま縦スクロール＋行仮想化で描画負荷を抑える。
const COLUMN_HEADER_HEIGHT = 56; // 列ヘッダー（standard density）
const ROW_HEIGHT = 52; // 行（standard density）
const PAGINATION_HEIGHT = 52; // 上下ページャ各々の高さ
const INITIAL_VISIBLE_ROWS = 10; // 初期ページサイズ。これを表示し切る高さにする。
// 末行が外枠ボーダーで欠けないよう数 px の余白を加える。
const GRID_HEIGHT =
  COLUMN_HEADER_HEIGHT +
  PAGINATION_HEIGHT * 2 +
  ROW_HEIGHT * INITIAL_VISIBLE_ROWS +
  4;

/**
 * テーブル上部のページャ。ツールバースロットに GridPagination を置き、
 * フッターと同じ操作・表示を上部にも出す（ページャは上下両方）。
 */
const TopPaginationToolbar: React.FC = () => (
  <GridToolbarContainer sx={{ justifyContent: "flex-end", px: 2 }}>
    <GridPagination />
  </GridToolbarContainer>
);

// DataGridPro に渡す参照を固定する（毎レンダリングで新規オブジェクトを生成しないため）。
const GRID_COMPONENTS = { Toolbar: TopPaginationToolbar };
const GRID_SX = { height: GRID_HEIGHT } as const;

/**
 * 取得履歴テーブル。既存の AcquireBookDataGrid（MUI DataGrid Pro ラッパー）を流用する（新規テーブルを作らない）。
 * - 表示列: 登記取得依頼日 / 登記取得日 / ステータス / 全部事項 / 所有者事項 / 電子公図 / 地積測量図 / 地役権図面 / 建物図面（土地物件では非表示）
 * - 並べ替え: 初期表示はサーバ降順（requestedAt 降順）。日時・ステータス列はユーザー操作でクライアントソート可能（DL 列は不可）
 * - ページネーション: 全件取得した list に対しクライアント側で行う（feature 内 useGridPagination）
 * - 履歴 0 件時: AcquireBookDataGrid（jaJP）の標準オーバーレイ「行がありません。」を表示する
 */
export const PropertyCodePictureRequestTable: React.FC<
  PropertyCodePictureRequestTableProps
> = ({ history, realEstateType, isLoading = false, resetPageSignal }) => {
  // DataGrid 必須の id に pictureRequestId を流用する。
  // 参照を安定させ、ページ送り等の再レンダリングで DataGrid の再計算を起こさない。
  const rows = useMemo<PropertyCodePictureRequestGridRow[]>(
    () =>
      (history?.list ?? []).map((row) => ({
        ...row,
        id: row.pictureRequestId,
      })),
    [history]
  );

  // columns は参照を保つ（MUI 推奨）。DL は DownloadCell の uri 直開きに委譲するため依存なし。
  const columns = useMemo(() => makeHistoryGridColDef(), []);
  // 建物図面列の表示可否は columnVisibilityModel で制御する。
  const columnVisibilityModel = useMemo(
    () => makeHistoryColumnVisibilityModel(realEstateType),
    [realEstateType]
  );

  // 全件取得（page/limit 未使用）に対するクライアント側ページネーション（初期10行）。
  const { page, pageSize, changePage, changePageSize, resetPage } =
    useGridPagination();

  const [sortModel, setSortModel] = useState<GridSortModel>([]);

  // 取得成功シグナルの変化でページ・ソートを既定へ戻す（初回マウント時は無視）。
  const prevResetSignalRef = useRef(resetPageSignal);
  useEffect(() => {
    if (prevResetSignalRef.current !== resetPageSignal) {
      prevResetSignalRef.current = resetPageSignal;
      resetPage();
      setSortModel([]);
    }
  }, [resetPageSignal, resetPage]);

  return (
    // 列追加（図面系）に備え横スクロール前提のレイアウトにする。
    <Box sx={{ width: "100%", overflowX: "auto" }}>
      <AcquireBookDataGrid
        rows={rows}
        columns={columns}
        columnVisibilityModel={columnVisibilityModel}
        loading={isLoading}
        disableColumnMenu
        disableSelectionOnClick
        hideFooterSelectedRowCount
        sortModel={sortModel}
        onSortModelChange={setSortModel}
        page={page}
        pageSize={pageSize}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        onPageChange={changePage}
        onPageSizeChange={changePageSize}
        sx={GRID_SX}
        components={GRID_COMPONENTS}
      />
    </Box>
  );
};
